/* ============================================================
 * frontend-v3/src/screens/operations/LiveRelay.tsx
 *
 * "Relay en vivo" — operator console for the `live-relay` service: one
 * Kubernetes Deployment per relay running `ffmpeg -c copy` from a live source
 * (HLS / RTMP / SRT) to a destination (RTMP such as YouTube Live, SRT, or HLS
 * over HTTP PUT). Kubernetes is the job record, so there is no Convex table:
 * every read here is an action round-trip to the service, proxied by
 * `convex/actions/liveRelayApi.ts` so the bearer token never reaches the
 * browser.
 *
 * Covers every /v1/relays operation in the service's OpenAPI document:
 * list, get (via list refresh), create, pause, resume, delete.
 *
 * Honest-state (CLAUDE.md §5): Skeleton while loading, EmptyState when the
 * namespace is empty, real backend errors surface as pills / toasts, and the
 * service's field-level validation errors land on the matching form field.
 * The destination URL is shown exactly as the service returns it: the stream
 * key is redacted server-side and never echoed back.
 * ============================================================ */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAction } from 'convex/react';
import { api } from '@convex/api';
import { toast } from 'sonner';
import {
  AdminGate,
  Btn,
  ConfirmModal,
  EmptyState,
  Field,
  Input,
  Modal,
  PageHead,
  Panel,
  Pill,
  Skeleton,
  Textarea,
} from '../../components/primitives';
import { useCurrentMember } from '../../hooks/useCurrentMember';

/* ---------------- Types (mirror the service's OpenAPI schemas) ---------------- */
type RelayState = 'starting' | 'running' | 'paused' | 'failing';

interface Relay {
  id: string;
  sourceUrl: string;
  destinationUrl: string; // redacted by the service
  ffmpegExtraArgs?: string[];
  state: RelayState;
  replicas: number;
  readyReplicas: number;
  restarts?: number;
  createdAt: string;
  deploymentName: string;
}

interface RelayApiError {
  status: number;
  error: string;
  message: string;
  fields?: Record<string, string>;
}

const POLL_MS = 10_000;
const RELAY_ID_PATTERN = /^[a-z0-9][a-z0-9-]{0,39}$/;
const SOURCE_SCHEMES = ['http:', 'https:', 'rtmp:', 'rtmps:', 'srt:'];
const DESTINATION_SCHEMES = ['rtmp:', 'rtmps:', 'srt:', 'http:', 'https:'];

const STATE_LABEL: Record<RelayState, { label: string; tone: string }> = {
  starting: { label: 'Iniciando', tone: 'warn' },
  running: { label: 'En marcha', tone: 'good' },
  paused: { label: 'En pausa', tone: 'neutral' },
  failing: { label: 'Fallando', tone: 'bad' },
};

/**
 * The action throws `Error(JSON.stringify(RelayApiError))`; Convex prefixes the
 * message ("Uncaught Error: …") and may append a stack, so extract the JSON
 * object from wherever it sits. Anything unparseable becomes a generic error.
 */
function parseRelayError(err: unknown): RelayApiError {
  const raw = err instanceof Error ? err.message : String(err);
  const start = raw.indexOf('{');
  const end = raw.lastIndexOf('}');
  if (start >= 0 && end > start) {
    try {
      const parsed = JSON.parse(raw.slice(start, end + 1)) as Partial<RelayApiError>;
      if (typeof parsed.message === 'string') {
        return {
          status: typeof parsed.status === 'number' ? parsed.status : 0,
          error: typeof parsed.error === 'string' ? parsed.error : 'internal',
          message: parsed.message,
          ...(parsed.fields ? { fields: parsed.fields } : {}),
        };
      }
    } catch {
      /* fall through */
    }
  }
  const stripped = raw.replace(/^Uncaught Error:\s*/, '').split('\n')[0] ?? raw;
  return { status: 0, error: 'internal', message: stripped || 'Error desconocido' };
}

function schemeOf(url: string): string | null {
  try {
    return new URL(url).protocol;
  } catch {
    return null;
  }
}

function fmtDate(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
}

/* ---------------- Screen ---------------- */
export function LiveRelay() {
  const member = useCurrentMember();
  const isAdmin = !!member && member.role === 'admin';
  return <AdminGate>{isAdmin ? <LiveRelayInner /> : null}</AdminGate>;
}

function LiveRelayInner() {
  const listRelays = useAction(api.actions.liveRelayApi.listRelays);
  const createRelay = useAction(api.actions.liveRelayApi.createRelay);
  const pauseRelay = useAction(api.actions.liveRelayApi.pauseRelay);
  const resumeRelay = useAction(api.actions.liveRelayApi.resumeRelay);
  const deleteRelay = useAction(api.actions.liveRelayApi.deleteRelay);

  // undefined = loading, null = load error, [] = empty namespace
  const [relays, setRelays] = useState<Relay[] | null | undefined>(undefined);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [reload, setReload] = useState(0);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [pendingDelete, setPendingDelete] = useState<Relay | null>(null);
  const [createOpen, setCreateOpen] = useState(false);

  const refresh = useCallback(() => setReload((k) => k + 1), []);

  // Initial load + polling: a relay is `starting` until its pod is ready and
  // `failing` only two minutes after creation, so the list must re-read.
  useEffect(() => {
    let alive = true;
    const run = () => {
      listRelays({})
        .then((r) => {
          if (!alive) return;
          setRelays(r as Relay[]);
          setLoadError(null);
        })
        .catch((e: unknown) => {
          if (!alive) return;
          setRelays((prev) => (prev === undefined ? null : prev));
          setLoadError(parseRelayError(e).message);
        });
    };
    run();
    const t = setInterval(run, POLL_MS);
    return () => {
      alive = false;
      clearInterval(t);
    };
  }, [listRelays, reload]);

  async function onPauseResume(r: Relay) {
    setBusyId(r.id);
    try {
      if (r.state === 'paused') {
        const next = (await resumeRelay({ id: r.id })) as Relay;
        setRelays((prev) => prev?.map((x) => (x.id === r.id ? next : x)) ?? prev);
        toast.success(`Relay ${r.id} reanudado`);
      } else {
        const next = (await pauseRelay({ id: r.id })) as Relay;
        setRelays((prev) => prev?.map((x) => (x.id === r.id ? next : x)) ?? prev);
        toast.success(`Relay ${r.id} en pausa`);
      }
    } catch (e) {
      toast.error(parseRelayError(e).message);
    } finally {
      setBusyId(null);
    }
  }

  async function onDelete(r: Relay) {
    setBusyId(r.id);
    setPendingDelete(null);
    try {
      await deleteRelay({ id: r.id });
      setRelays((prev) => prev?.filter((x) => x.id !== r.id) ?? prev);
      toast.success(`Relay ${r.id} eliminado`);
    } catch (e) {
      toast.error(parseRelayError(e).message);
    } finally {
      setBusyId(null);
    }
  }

  const counts = useMemo(() => {
    const c: Record<RelayState, number> = { starting: 0, running: 0, paused: 0, failing: 0 };
    for (const r of relays ?? []) c[r.state] = (c[r.state] ?? 0) + 1;
    return c;
  }, [relays]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <PageHead
        crumbs={[{ label: 'Operaciones' }, { label: 'Relay en vivo' }]}
        title="Relay en vivo"
        description="Retransmite una fuente en vivo (HLS, RTMP o SRT) hacia un destino (RTMP como YouTube Live, SRT o HLS por HTTP PUT) sin recodificar. Cada relay es un Deployment de Kubernetes."
      />

      <Panel
        title="Relays"
        reviewed="wired"
        right={
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            {relays && relays.length > 0 && (
              <span style={{ display: 'flex', gap: 6 }}>
                {(Object.keys(STATE_LABEL) as RelayState[])
                  .filter((s) => counts[s] > 0)
                  .map((s) => (
                    <Pill key={s} tone={STATE_LABEL[s].tone} size="sm" dot reviewed="wired">
                      {counts[s]} {STATE_LABEL[s].label.toLowerCase()}
                    </Pill>
                  ))}
              </span>
            )}
            <Btn variant="ghost" size="sm" icon="refresh" reviewed="wired" onClick={refresh}>
              Actualizar
            </Btn>
            <Btn
              variant="solid"
              size="sm"
              icon="plus"
              reviewed="wired"
              onClick={() => setCreateOpen(true)}
            >
              Nuevo relay
            </Btn>
          </div>
        }
      >
        {loadError && relays !== null && (
          <div style={{ marginBottom: 12 }}>
            <Pill tone="warn" size="sm" reviewed="wired">
              Última actualización falló: {loadError}
            </Pill>
          </div>
        )}
        {relays === undefined ? (
          <Skeleton h={140} />
        ) : relays === null ? (
          <Pill tone="bad" size="sm" reviewed="wired">
            No se pudieron cargar los relays — {loadError ?? 'verifica la conexión con live-relay'}
          </Pill>
        ) : relays.length === 0 ? (
          <EmptyState
            icon="send"
            title="Sin relays"
            body="Crea un relay para retransmitir una fuente en vivo hacia un destino."
          />
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            {relays.map((r) => {
              const st = STATE_LABEL[r.state] ?? { label: r.state, tone: 'neutral' };
              const busy = busyId === r.id;
              return (
                <div
                  key={r.id}
                  data-reviewed="wired"
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'minmax(140px, 1fr) minmax(0, 2fr) auto',
                    alignItems: 'center',
                    gap: 16,
                    padding: '12px 0',
                    borderBottom: '1px solid var(--border-cool)',
                  }}
                >
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0 }}>
                    <strong className="t-mono" style={{ fontSize: 14, textTransform: 'none' }}>
                      {r.id}
                    </strong>
                    <span style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                      <Pill tone={st.tone} size="sm" dot>
                        {st.label}
                      </Pill>
                      <span
                        className="t-mono"
                        style={{ fontSize: 11, color: 'var(--muted-slate)', textTransform: 'none' }}
                        title="Réplicas listas / deseadas"
                      >
                        {r.readyReplicas}/{r.replicas} listas
                      </span>
                    </span>
                    <span style={{ fontSize: 11, color: 'var(--muted-slate)' }}>
                      Creado {fmtDate(r.createdAt)} · {r.deploymentName}
                    </span>
                  </div>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 }}>
                    <UrlLine label="Fuente" value={r.sourceUrl} />
                    <UrlLine label="Destino" value={r.destinationUrl} />
                    {r.ffmpegExtraArgs && r.ffmpegExtraArgs.length > 0 && (
                      <UrlLine label="ffmpeg" value={r.ffmpegExtraArgs.join(' ')} />
                    )}
                  </div>

                  <div style={{ display: 'flex', gap: 8 }}>
                    <Btn
                      variant="ghost"
                      size="sm"
                      icon={r.state === 'paused' ? 'play' : 'pause'}
                      reviewed="wired"
                      loading={busy}
                      disabled={busyId !== null && !busy}
                      onClick={() => { void onPauseResume(r); }}
                    >
                      {r.state === 'paused' ? 'Reanudar' : 'Pausar'}
                    </Btn>
                    <Btn
                      variant="danger"
                      size="sm"
                      icon="trash"
                      reviewed="wired"
                      disabled={busyId !== null}
                      onClick={() => setPendingDelete(r)}
                    >
                      Eliminar
                    </Btn>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Panel>

      <CreateRelayModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onSubmit={async (input) => {
          const created = (await createRelay(input)) as Relay;
          setRelays((prev) => [...(prev ?? []).filter((x) => x.id !== created.id), created].sort((a, b) => a.id.localeCompare(b.id)));
          toast.success(`Relay ${created.id} creado — iniciando`);
        }}
      />

      <ConfirmModal
        open={pendingDelete !== null}
        onClose={() => setPendingDelete(null)}
        title="Eliminar relay"
        danger
        typed="ELIMINAR"
        confirmLabel="Eliminar"
        body={
          pendingDelete === null ? null : (
            <span>
              Se detendrá y eliminará el relay <strong className="t-mono">{pendingDelete.id}</strong>
              {pendingDelete.state === 'running' && (
                <>
                  {' '}
                  <strong>que está en marcha</strong>: el destino dejará de recibir señal de inmediato
                </>
              )}
              . El Deployment y el Secret del destino se borran con él. Para reanudarlo más tarde
              sin perder la configuración, usa <strong>Pausar</strong> en su lugar.
            </span>
          )
        }
        onConfirm={() => { if (pendingDelete) void onDelete(pendingDelete); }}
      />
    </div>
  );
}

function UrlLine({ label, value }: { label: string; value: string }) {
  return (
    <span style={{ display: 'flex', gap: 8, alignItems: 'baseline', minWidth: 0 }}>
      <span style={{ fontSize: 11, color: 'var(--muted-slate)', width: 52, flexShrink: 0 }}>{label}</span>
      <span
        className="t-mono"
        title={value}
        style={{
          fontSize: 12,
          color: 'var(--ink-muted)',
          textTransform: 'none',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {value}
      </span>
    </span>
  );
}

/* ---------------- Create form ---------------- */
interface CreateInput {
  id?: string;
  sourceUrl: string;
  destinationUrl: string;
  ffmpegExtraArgs?: string[];
}

function CreateRelayModal({
  open,
  onClose,
  onSubmit,
}: {
  open: boolean;
  onClose: () => void;
  onSubmit: (input: CreateInput) => Promise<void>;
}) {
  const [id, setId] = useState('');
  const [sourceUrl, setSourceUrl] = useState('');
  const [destinationUrl, setDestinationUrl] = useState('');
  const [extraArgs, setExtraArgs] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setId('');
    setSourceUrl('');
    setDestinationUrl('');
    setExtraArgs('');
    setErrors({});
    setFormError(null);
    setBusy(false);
  }, [open]);

  function validate(): Record<string, string> {
    const e: Record<string, string> = {};
    if (id.trim() && !RELAY_ID_PATTERN.test(id.trim())) {
      e.id = '1-40 minúsculas, dígitos o guiones; empieza por letra o dígito';
    }
    const ss = schemeOf(sourceUrl.trim());
    if (!sourceUrl.trim()) e.sourceUrl = 'Obligatorio';
    else if (!ss || !SOURCE_SCHEMES.includes(ss)) e.sourceUrl = 'Esquemas permitidos: http, https, rtmp, rtmps, srt';
    const ds = schemeOf(destinationUrl.trim());
    if (!destinationUrl.trim()) e.destinationUrl = 'Obligatorio';
    else if (!ds || !DESTINATION_SCHEMES.includes(ds)) e.destinationUrl = 'Esquemas permitidos: rtmp, rtmps, srt, http, https';
    const args = extraArgs.split('\n').map((s) => s.trim()).filter(Boolean);
    const bad = args.findIndex((a) => /[\x00-\x1f\x7f]/.test(a));
    if (bad >= 0) e.ffmpegExtraArgs = `El argumento ${bad + 1} contiene caracteres de control`;
    return e;
  }

  async function submit() {
    const e = validate();
    setErrors(e);
    setFormError(null);
    if (Object.keys(e).length > 0) return;
    const args = extraArgs.split('\n').map((s) => s.trim()).filter(Boolean);
    setBusy(true);
    try {
      await onSubmit({
        ...(id.trim() ? { id: id.trim() } : {}),
        sourceUrl: sourceUrl.trim(),
        destinationUrl: destinationUrl.trim(),
        ...(args.length > 0 ? { ffmpegExtraArgs: args } : {}),
      });
      onClose();
    } catch (err) {
      const apiErr = parseRelayError(err);
      if (apiErr.fields) {
        // `ffmpegExtraArgs[n]` keys collapse onto the textarea.
        const mapped: Record<string, string> = {};
        for (const [k, m] of Object.entries(apiErr.fields)) {
          mapped[k.startsWith('ffmpegExtraArgs') ? 'ffmpegExtraArgs' : k] = m;
        }
        setErrors(mapped);
      }
      setFormError(
        apiErr.error === 'already_exists'
          ? 'Ya existe un relay con ese id'
          : apiErr.error === 'too_many_relays'
            ? 'Se alcanzó el máximo de relays del namespace'
            : apiErr.error === 'kubernetes_timeout'
              ? 'El clúster no respondió a tiempo — reintenta'
              : apiErr.fields
                ? 'Revisa los campos marcados'
                : apiErr.message,
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={() => { if (!busy) onClose(); }}
      title="Nuevo relay"
      width={600}
      footer={
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <Btn variant="ghost" size="sm" reviewed="wired" disabled={busy} onClick={onClose}>
            Cancelar
          </Btn>
          <Btn variant="primary" size="sm" icon="play" reviewed="wired" loading={busy} onClick={() => { void submit(); }}>
            Crear relay
          </Btn>
        </div>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {formError && (
          <Pill tone="bad" size="sm" reviewed="wired">
            {formError}
          </Pill>
        )}
        <Field
          label="Id"
          hint="Opcional; se genera si se omite. Será el nombre del Deployment relay-<id>."
          error={errors.id}
        >
          <Input value={id} onChange={setId} placeholder="yt-main" reviewed="wired" disabled={busy} autoFocus />
        </Field>
        <Field
          label="URL de la fuente"
          required
          hint="Playlist HLS (http/https), rtmp, rtmps o srt."
          error={errors.sourceUrl}
        >
          <Input
            value={sourceUrl}
            onChange={setSourceUrl}
            placeholder="https://cdn.example.com/live/master.m3u8"
            reviewed="wired"
            disabled={busy}
          />
        </Field>
        <Field
          label="URL del destino"
          required
          hint="Incluye la clave de transmisión. Se guarda en un Secret y nunca se vuelve a mostrar completa."
          error={errors.destinationUrl}
        >
          <Input
            value={destinationUrl}
            onChange={setDestinationUrl}
            placeholder="rtmp://a.rtmp.youtube.com/live2/xxxx-xxxx-xxxx-xxxx"
            reviewed="wired"
            disabled={busy}
          />
        </Field>
        <Field
          label="Argumentos extra de ffmpeg"
          hint="Uno por línea. Se añaden tras -c copy y antes de las opciones de salida, sin interpretación de shell."
          error={errors.ffmpegExtraArgs}
        >
          <Textarea
            value={extraArgs}
            onChange={setExtraArgs}
            placeholder={'-rtmp_live\nlive'}
            rows={3}
            reviewed="wired"
          />
        </Field>
      </div>
    </Modal>
  );
}

export default LiveRelay;
